import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
  setUser: () => {}
});

export const useAuth = () => useContext(AuthContext);

// Non-sensitive user profile cache (id, name, email) for fast first-paint of the UI
// before /api/auth/me responds. Cleared on tab close. The actual session lives in an
// httpOnly cookie that JavaScript cannot read.
const USER_CACHE_KEY = 'overtimeiq_user';

const getCachedUser = () => {
  try {
    const data = sessionStorage.getItem(USER_CACHE_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const cacheUser = (userData) => {
  try {
    if (userData) {
      sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    } else {
      sessionStorage.removeItem(USER_CACHE_KEY);
    }
  } catch {
    // sessionStorage may be unavailable in private mode — non-critical, UI still works via /me
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isLoggingIn = useRef(false);

  const checkAuth = useCallback(async () => {
    if (isLoggingIn.current) return;

    try {
      // Show cached user immediately for fast UI while /me round-trips
      const cached = getCachedUser();
      if (cached) setUser(cached);

      // Validate session via httpOnly cookie
      const response = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true,
        timeout: 10000
      });

      if (response.data) {
        setUser(response.data);
        cacheUser(response.data);
      } else {
        setUser(null);
        cacheUser(null);
      }
    } catch (error) {
      // Session invalid or expired — clear cached profile
      if (process.env.NODE_ENV === 'development') {
        console.warn('Auth check failed:', error.message);
      }
      setUser(null);
      cacheUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (userData) => {
    isLoggingIn.current = true;
    try {
      setUser(userData);
      cacheUser(userData);
      // Cookie was set by backend on /login response. Nothing to store client-side.
    } finally {
      // Release the lock after auth state settles
      setTimeout(() => { isLoggingIn.current = false; }, 500);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, {
        withCredentials: true,
        timeout: 10000
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Logout API call failed:', error.message);
      }
    } finally {
      setUser(null);
      cacheUser(null);
    }
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    setUser: login
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
